import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ChevronRight, Plus, Trash2 } from 'lucide-react'
import { api } from '@/lib/api/client'
import type { PublicHoliday } from '@/types/api'

const CURRENT_YEAR = new Date().getFullYear()
const YEARS        = [CURRENT_YEAR - 1, CURRENT_YEAR, CURRENT_YEAR + 1, CURRENT_YEAR + 2]

function Breadcrumb() {
  return (
    <div className="row" style={{ gap: 6, fontSize: 13, color: 'var(--ns-text-muted)', marginBottom: 20 }}>
      <Link to="/settings/integrations" style={{ color: 'var(--ns-text-muted)', textDecoration: 'none' }}>Settings</Link>
      <ChevronRight size={14} />
      <span style={{ color: 'var(--ns-text)' }}>Public Holidays</span>
    </div>
  )
}

export default function PublicHolidaysPage() {
  const queryClient = useQueryClient()
  const [year,    setYear]    = useState(CURRENT_YEAR)
  const [country, setCountry] = useState('SE')
  const [adding,  setAdding]  = useState(false)

  const [newDate,    setNewDate]    = useState('')
  const [newName,    setNewName]    = useState('')
  const [newCountry, setNewCountry] = useState('SE')

  const { data: holidays = [], isLoading } = useQuery({
    queryKey: ['admin', 'public-holidays', year, country],
    queryFn:  () => api.get<PublicHoliday[]>(`/settings/public-holidays?year=${year}&countryCode=${country}`),
    staleTime: 60 * 60 * 1000,
  })

  const addMutation = useMutation({
    mutationFn: () => api.post<{ id: number }>('/settings/public-holidays', {
      date: newDate, name: newName, countryCode: newCountry,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'public-holidays'] })
      queryClient.invalidateQueries({ queryKey: ['time', 'my-available-hours'] })
      setAdding(false)
      setNewDate('')
      setNewName('')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.delete<unknown>(`/settings/public-holidays/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'public-holidays'] })
      queryClient.invalidateQueries({ queryKey: ['time', 'my-available-hours'] })
    },
  })

  return (
    <div className="page">
      <Breadcrumb />
      <div className="page-header">
        <div>
          <h1 className="page-title">Public Holidays</h1>
          <p className="page-subtitle">
            Manage public holidays used to calculate available hours per employee.
            Country defaults to <strong>SE</strong> (Sweden).
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="row" style={{ gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={{ fontSize: 13, color: 'var(--ns-text-muted)' }}>Year</label>
          <select
            className="input"
            style={{ width: 100 }}
            value={year}
            onChange={e => setYear(Number(e.target.value))}
          >
            {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={{ fontSize: 13, color: 'var(--ns-text-muted)' }}>Country</label>
          <input
            className="input"
            style={{ width: 72 }}
            value={country}
            maxLength={2}
            onChange={e => setCountry(e.target.value.toUpperCase())}
          />
        </div>
        <button className="btn" style={{ marginLeft: 'auto' }} onClick={() => setAdding(true)}>
          <Plus size={14} /> Add holiday
        </button>
      </div>

      {/* Add form */}
      {adding && (
        <div className="card" style={{ padding: 16, marginBottom: 16 }}>
          <div className="row" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ fontSize: 12, color: 'var(--ns-text-muted)' }}>Date</label>
              <input
                type="date"
                className="input"
                value={newDate}
                onChange={e => setNewDate(e.target.value)}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 200 }}>
              <label style={{ fontSize: 12, color: 'var(--ns-text-muted)' }}>Name</label>
              <input
                className="input"
                placeholder="e.g. Midsommardagen"
                value={newName}
                onChange={e => setNewName(e.target.value)}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ fontSize: 12, color: 'var(--ns-text-muted)' }}>Country</label>
              <input
                className="input"
                style={{ width: 64 }}
                value={newCountry}
                maxLength={2}
                onChange={e => setNewCountry(e.target.value.toUpperCase())}
              />
            </div>
            <div className="row" style={{ gap: 8 }}>
              <button
                className="btn btn-primary"
                disabled={!newDate || !newName || addMutation.isPending}
                onClick={() => addMutation.mutate()}
              >
                {addMutation.isPending ? 'Saving…' : 'Save'}
              </button>
              <button className="btn" onClick={() => setAdding(false)}>Cancel</button>
            </div>
          </div>
          {addMutation.isError && (
            <p style={{ marginTop: 8, fontSize: 12, color: 'oklch(0.55 0.18 30)' }}>
              Failed to save — check date format and that the holiday doesn't already exist.
            </p>
          )}
        </div>
      )}

      {/* Table */}
      {isLoading ? (
        <p className="muted" style={{ textAlign: 'center', padding: 32 }}>Loading…</p>
      ) : (
        <div className="card" style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Name</th>
                <th>Country</th>
                <th style={{ width: 52 }} />
              </tr>
            </thead>
            <tbody>
              {holidays.length === 0 && (
                <tr>
                  <td colSpan={4} style={{ textAlign: 'center', color: 'var(--ns-text-muted)', padding: 24 }}>
                    No holidays found for {year} / {country}.
                  </td>
                </tr>
              )}
              {holidays.map(h => (
                <tr key={h.id}>
                  <td className="mono tabular">{h.date}</td>
                  <td>{h.name}</td>
                  <td>
                    <span className="badge">{h.countryCode}</span>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <button
                      className="btn btn-icon"
                      title="Delete"
                      disabled={deleteMutation.isPending}
                      onClick={() => {
                        if (!confirm(`Delete "${h.name}" (${h.date})?`)) return
                        deleteMutation.mutate(h.id)
                      }}
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
